import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { loadAllAndAssert, loadAndAssert } from '../authz/access.js';
import { Exam } from '../exam.js';
import { aggregateOCRFromBoundingBoxes } from '../utils/exam-utils.js';
import { createBoundingBoxWithOCR } from './agents/extraction-modules/ocr-utils.js';
import { OcrExtractionType } from './agents/extraction-modules/ocrExtractionTypes.js';
import { TaskType } from './task-types/task-main.js';

interface NestedTask {
  _id?: string;
  taskNumber?: string;
  taskType?: string;
  description?: string;
  boundingBoxes?: any[];
  subTasks?: NestedTask[];
}

async function updateBoundingBoxesRecursively(
  tasks: NestedTask[] | undefined,
  boxIds: Set<string>,
  updater: (box: any, taskType: string) => Promise<void>,
  onMatchedBox?: (boxId: string) => void,
): Promise<boolean> {
  if (!Array.isArray(tasks)) return false;

  for (const task of tasks) {
    let taskChanged = false;

    if (Array.isArray(task?.boundingBoxes)) {
      for (const box of task.boundingBoxes) {
        const boxId = String(box?._id ?? box?.id ?? '');
        if (boxIds.has(boxId)) {
          onMatchedBox?.(boxId);
          await updater(box, task?.taskType || TaskType.DEFAULT);
          taskChanged = true;
        }
      }

      if (taskChanged) {
        task.description = aggregateOCRFromBoundingBoxes(task.boundingBoxes as any);
      }
    }

    if (Array.isArray(task?.subTasks)) {
      await updateBoundingBoxesRecursively(task.subTasks, boxIds, updater, onMatchedBox);
    }
  }

  return true;
}

export const rerunBoundingBoxOCRMutation = registerMutation({
  operationName: 'rerunBoundingBoxOCRMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'boundingBoxIds', type: () => [String] },
    { name: 'imageIds', type: () => [String] },
    { name: 'updatedBoxesJson', type: () => String },
  ],
  resolve:
    (examId: string, boundingBoxIds: string[], imageIds: string[] = [], updatedBoxesJson = '[]') =>
    async () => {
      if (!Array.isArray(boundingBoxIds) || boundingBoxIds.length === 0) {
        return true; // Nothing to do
      }

      logger.info({
        examId,
        requestedBoxIds: boundingBoxIds,
        requestedBoxCount: boundingBoxIds.length,
        imageIdsCount: Array.isArray(imageIds) ? imageIds.length : 0,
        context: 'StructureEditor Debug',
        operation: 'rerunBoundingBoxOCRMutation',
      }, 'start');

      await loadAndAssert(Exam, examId, 'update', { _id: 1 });
      // The images are OCR'd (sent to the LLM) — only files the caller may read.
      await loadAllAndAssert(AdornisFile, imageIds, 'read', { _id: 1 });
      const examCollection = await getCollection(Exam as any);
      const exam = await examCollection.findOne({ _id: examId } as any);

      if (!exam) {
        throw new Error(`Exam ${examId} not found`);
      }

      const boxIdSet = new Set(boundingBoxIds.map(id => String(id)));
      const imageArray = imageIds || [];
      let updatedBoxes: Array<{
        id: string;
        pageNumber?: number;
        bbox?: { x1: number; y1: number; x2: number; y2: number };
      }> = [];
      try {
        const parsed = JSON.parse(String(updatedBoxesJson || '[]'));
        if (Array.isArray(parsed)) updatedBoxes = parsed as any;
      } catch {
        updatedBoxes = [];
      }
      const updatedById = new Map<string, any>(
        updatedBoxes.map(item => [String((item as any)?.id ?? '').trim(), item] as const).filter(([id]) => Boolean(id)),
      );
      const matchedBoxIds = new Set<string>();
      let ocrUpdates = 0;
      let emptyOcrAfterUpdate = 0;

      // Update boxes in place without rebuilding entire task structure
      await updateBoundingBoxesRecursively(
        exam.tasks as NestedTask[] | undefined,
        boxIdSet,
        async (box: any, taskType: string) => {
          const boxId = String(box?._id ?? box?.id ?? '');
          const updated = updatedById.get(boxId);
          if (updated?.bbox) {
            const ux1 = Number(updated.bbox.x1);
            const uy1 = Number(updated.bbox.y1);
            const ux2 = Number(updated.bbox.x2);
            const uy2 = Number(updated.bbox.y2);
            if ([ux1, uy1, ux2, uy2].every(Number.isFinite)) {
              const left = Math.max(0, Math.min(ux1, ux2));
              const top = Math.max(0, Math.min(uy1, uy2));
              const right = Math.min(1000, Math.max(ux1, ux2));
              const bottom = Math.min(1000, Math.max(uy1, uy2));
              if (right > left && bottom > top) {
                box.topLeft = { x: left, y: top };
                box.bottomRight = { x: right, y: bottom };
              }
            }

            const updatedPageNumber = Number(updated?.pageNumber);
            if (Number.isFinite(updatedPageNumber) && updatedPageNumber >= 1) {
              box.pageNumber = Math.round(updatedPageNumber);
            }
          }

          const topLeft = box?.topLeft;
          const bottomRight = box?.bottomRight;
          const hasCoordinates =
            typeof topLeft?.x === 'number' &&
            typeof topLeft?.y === 'number' &&
            typeof bottomRight?.x === 'number' &&
            typeof bottomRight?.y === 'number';

          if (!hasCoordinates) {
            return;
          }

          if (box?.approved === true) {
            return;
          }

          let updatedBox;
          try {
            updatedBox = await createBoundingBoxWithOCR(
              {
                boundingBoxId: boxId,
                pageNumber: Math.max(1, Number(box?.pageNumber ?? 1)),
                confidence: typeof box?.confidence === 'number' ? box.confidence : undefined,
                bbox: {
                  x1: topLeft.x,
                  y1: topLeft.y,
                  x2: bottomRight.x,
                  y2: bottomRight.y,
                },
              },
              imageArray,
              OcrExtractionType.TASK_DESCRIPTION,
              taskType || TaskType.DEFAULT,
            );
          } catch (error) {
            // Don't let one box's OCR failure abort the whole geometry-edit save — the new
            // position (already written onto `box` above) would be lost along with it.
            logger.error({ err: error, boxId, examId }, 'OCR failed for box during rerun, keeping new geometry');
            return;
          }

          // Update the box in place
          Object.assign(box, updatedBox.toObject?.() ?? updatedBox);
          ocrUpdates += 1;
          if (!String(box?.ocrExtractedText ?? '').trim()) {
            emptyOcrAfterUpdate += 1;
          }
        },
        (matchedId: string) => {
          matchedBoxIds.add(matchedId);
        },
      );
      if (matchedBoxIds.size === 0) {
        logger.warn(
          {
            examId,
            requestedBoxIds: boundingBoxIds,
            context: 'StructureEditor Debug',
            operation: 'rerunBoundingBoxOCRMutation',
          },
          'No requested box IDs were found in persisted exam tasks. OCR-only update cannot run for unsaved/new boxes.',
        );
        return false;
      }

      // Save the updated exam
      await examCollection.updateOne({ _id: examId } as any, {
        $set: {
          tasks: exam.tasks,
        },
      });

      logger.info({
        examId,
        matchedBoxIdsCount: matchedBoxIds.size,
        matchedBoxIds: Array.from(matchedBoxIds),
        updatedBoxesPayloadCount: updatedById.size,
        ocrUpdates,
        emptyOcrAfterUpdate,
        topLevelTaskCount: Array.isArray((exam as any)?.tasks) ? (exam as any).tasks.length : 0,
        context: 'StructureEditor Debug',
        operation: 'rerunBoundingBoxOCRMutation',
      }, 'summary');

      return true;
    },
});
